import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {getBenefitAmountGVA6} from 'Quoting/utils/utilities';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {handleRestrictPlanOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {DailyHospitalConfinement} from './DailyHospitalConfinement';
import {InitialHospitalAdmission} from './InitialHospitalAdmission';
import {IntensiveCareUnitConfinement} from './IntensiveCareUnitConfinement';
import {ObjectiveSecondOpinion} from './ObjectiveSecondOpinion';
import {accidentFacilityCarePath} from './PropertyPaths/accidentFacilityCarePath';
import {dislocationFractureRiderPath} from './PropertyPaths/dislocationFractureRiderPath';
import {accidentBenefitsUpdateFields} from './PropertyPaths/propertyPathACC';
import {
  addPathtoUpdate,
  buildAccidentDeathDismembermentBenefitPaths,
  buildAccidentTreatementUrgentCareBenefitPaths,
  buildAllUnitsPaths,
  buildBenefitEnhancementBenefitPaths,
  buildDislocationFratureRiderBenefitPaths,
  buildHospitalBenefitPaths,
} from './updateGVA6BenefitAmounts';
import {List as ImmutableList} from 'immutable';

export const HospitalBenefitsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValues, updateQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);
  const {selectedPlan, defaultPlanValues} = state.quoteForm;
  const propertyPaths = accidentFacilityCarePath(selectedPlan?.planIndex || 0, 0);
  const accidentBenefitsUpdateUnitsPath = accidentBenefitsUpdateFields(selectedPlan?.planIndex || 0, 0);
  const dislocationPaths = dislocationFractureRiderPath(selectedPlan?.planIndex ?? 0);

  const dislocationRiderType = getQuoteFieldValue(
    dislocationPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider
  );

  const [planUnitsOptions, setPlanUnitsOptions] = useState<any[]>([]);
  const numOfPlans = getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')?.size || 0;


  const updateAllUnitsAndBenefits = (units: number) => {
    if (!isNaN(units)) {
      let valuesToUpdate:Array<{path:string, newValue: any}> = [];
      valuesToUpdate = addPathtoUpdate(accidentBenefitsUpdateUnitsPath.unitsOfAccidentFacilityCare, units, valuesToUpdate);
      valuesToUpdate = buildAllUnitsPaths(units, accidentBenefitsUpdateUnitsPath, valuesToUpdate);
      valuesToUpdate = buildHospitalBenefitPaths(units, propertyPaths, defaultPlanValues, valuesToUpdate);
     valuesToUpdate =  addPathtoUpdate(
        accidentBenefitsUpdateUnitsPath.emergencyRoomServicesDetailsBenefitAmount,
        units * getBenefitAmountGVA6(defaultPlanValues, 'ACCEmergencyRoomServicesBenefit.standardBenefitAmount')
      , valuesToUpdate);
      valuesToUpdate = buildAccidentTreatementUrgentCareBenefitPaths(units, accidentBenefitsUpdateUnitsPath, defaultPlanValues, valuesToUpdate);
      valuesToUpdate = buildBenefitEnhancementBenefitPaths(units, accidentBenefitsUpdateUnitsPath, defaultPlanValues, valuesToUpdate);
      valuesToUpdate = buildAccidentDeathDismembermentBenefitPaths(units, accidentBenefitsUpdateUnitsPath, defaultPlanValues, valuesToUpdate);
      valuesToUpdate = buildDislocationFratureRiderBenefitPaths(units, accidentBenefitsUpdateUnitsPath, dislocationRiderType, valuesToUpdate);
      updateQuoteFieldValues(valuesToUpdate)
    }
  };
  
  useEffect(() => {
    setPlanUnitsOptions(handleRestrictPlanOptions(numOfPlans, getQuoteFieldValue));
    const units = Number(getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare));
    updateAllUnitsAndBenefits(units);
  }, []);

  
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'hospital-benefits-units-row-1'},
      items: [
        {
          key: `hospital-benefits-units-${selectedPlan?.planIndex}`,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare)}
              onChange={e => {
                if (!isNaN(Number(e.target.value))) {
                  const units = Number(e.target.value);
                  updateAllUnitsAndBenefits(units);
                }
              }}
              id={`hospital-benefits-units-${selectedPlan?.planIndex}`}
              required
              options={planUnitsOptions}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  const hospitalBenefits: ConditionalSection[] = [
    {
      Component: InitialHospitalAdmission,
      key: 'InitialHospitalAdmission',
      label: 'INITIAL HOSPITAL ADMISSION',
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare) > 1,
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isHospitalAdmissionApplied, isApplied);
      },
    },
    {
      Component: DailyHospitalConfinement,
      key: 'DailyHospitalConfinement',
      label: 'DAILY HOSPITAL CONFINEMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isHospitalConfinementApplied, isApplied);
      },
    },
    {
      Component: IntensiveCareUnitConfinement,
      key: 'IntensiveCareUnitConfinement',
      label: 'INTENSIVE CARE UNIT CONFINEMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isIntensiveCareUnitConfinementApplied, isApplied);
      },
    },
    {
      Component: ObjectiveSecondOpinion,
      hidden:
        !getQuoteFieldValue<boolean>(propertyPaths.isObjectiveSecondOpinionApplied) ||
        !(getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare) > 1),
      key: 'objectiveSecondOpinion',
      label: 'OBJECTIVE SECOND OPINION (MD Only)',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isObjectiveSecondOpinionApplied),
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isObjectiveSecondOpinionApplied, isApplied);
      },
    },
  ];

  return (
    <FormWrapper title="Hospital Benefits" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={hospitalBenefits} />
      </Grid>
    </FormWrapper>
  );
};
